import React, { useState, useEffect, useCallback } from 'react';
import { ArrowDownTrayIcon, ArrowLeftIcon } from '@heroicons/react/24/outline';
import { axiosInstance } from '../../../../../routes/axiosInstance';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { selectBlurSensitive } from '../../../../../slices/preferencesSlice';
import { SLIDE_OVER_TOP_OFFSET_STYLE } from '../../../../../components/slideOverTransition';
import ExcelJS from 'exceljs';
import moment from 'moment';

/**
 * Market Report Viewer - Full Page
 * Downloads and parses Excel file to display in a full page view
 */
export const MarketReportViewer = ({ item, resourceName, onClose, onDownload }) => {
  const [loading, setLoading] = useState(true);
  const [reportData, setReportData] = useState(null);
  const [sheetNames, setSheetNames] = useState([]);
  const [activeSheet, setActiveSheet] = useState(0);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('summary'); // 'summary' or 'full'
  const blurSensitive = useSelector(selectBlurSensitive);

  // Helper function to convert Date object to Excel serial number
  const dateToExcelSerial = (date) => {
    const excelEpoch = new Date(1899, 11, 30);
    const msPerDay = 24 * 60 * 60 * 1000;
    return (date.getTime() - excelEpoch.getTime()) / msPerDay;
  };

  const fetchAndParseExcel = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // Construct the download URL for exports
      const status = (item.status || item.state || '').toString().toLowerCase();
      const id = item.id || item.server_id || item.job_id;

      let fileUrl;
      if ((status === 'completed' || status === 'ready') && id) {
        fileUrl = `/data/exports/${id}/download`;
      } else {
        fileUrl = item.file_url || item.download_url || item.fileUrl || item.url;
      }

      if (!fileUrl) {
        throw new Error('File URL not available');
      }

      const response = await axiosInstance.get(fileUrl, {
        responseType: 'arraybuffer'
      });

      // Parse the Excel file using ExcelJS
      const workbook = new ExcelJS.Workbook();
      await workbook.xlsx.load(response.data);

      // Get all sheet names
      const sheetNames = workbook.worksheets.map(sheet => sheet.name);
      setSheetNames(sheetNames);

      // Parse all sheets
      const parsedSheets = {};
      workbook.worksheets.forEach(worksheet => {
        const jsonData = [];

        // Iterate through all rows (including empty ones)
        worksheet.eachRow({ includeEmpty: true }, (row, rowNumber) => {
          // ExcelJS row.values has a leading undefined at index 0
          // We need to slice it to get an array starting from index 0
          const rowValues = [];
          row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
            let cellValue = cell.value;

            // ExcelJS returns Date objects for date cells
            // Convert them to Excel serial numbers to maintain compatibility
            // with the existing formatCellValue logic
            if (cellValue instanceof Date) {
              cellValue = dateToExcelSerial(cellValue);
            }

            rowValues[colNumber - 1] = cellValue;
          });

          jsonData.push(rowValues);
        });

        parsedSheets[worksheet.name] = jsonData;
      });

      setReportData(parsedSheets);
      setActiveSheet(0);
    } catch (err) {
      console.error('Failed to fetch and parse Excel file:', err);
      setError(err?.message || 'Failed to load Excel file');
      toast.error('Failed to load Excel file');
    } finally {
      setLoading(false);
    }
  }, [item]);

  useEffect(() => {
    fetchAndParseExcel();
  }, [fetchAndParseExcel]);

  // Helper function to convert Excel serial date to formatted date string
  const convertExcelDate = (serialNumber) => {
    if (typeof serialNumber !== 'number') return null;

    // Excel serial dates are typically in the range 1-50000+
    // Exclude very small numbers that are likely actual data values
    if (serialNumber < 1 || serialNumber > 100000) return null;

    try {
      // Excel's epoch is December 30, 1899
      // Excel serial dates are the number of days since this epoch
      const excelEpoch = new Date(1899, 11, 30); // Month is 0-indexed, so 11 = December
      const msPerDay = 24 * 60 * 60 * 1000;
      const date = new Date(excelEpoch.getTime() + serialNumber * msPerDay);

      // Check if the resulting date is valid
      if (isNaN(date.getTime())) return null;

      return moment(date).format('DD/MM/YYYY');
    } catch (e) {
      return null;
    }
  };

  // Helper function to check if a column should have date values
  // Only columns with TEXT headers containing date keywords should format values as dates
  const isDateColumn = (header) => {
    // Numeric headers are dates themselves, but their VALUES are not dates
    if (typeof header === 'number') {
      return false;
    }

    // Check if header text contains date keywords
    const headerStr = String(header).toLowerCase();
    return headerStr.includes('date') || headerStr.includes('time') ||
           headerStr.includes('day') || headerStr.includes('period');
  };

  // Helper function to format header
  const formatHeader = (header, idx) => {
    if (header === undefined || header === null || header === '') {
      return `Column ${idx + 1}`;
    }

    // Try to convert numeric headers to dates
    if (typeof header === 'number') {
      const dateStr = convertExcelDate(header);
      if (dateStr) return dateStr;
    }

    return header;
  };

  // Helper function to format cell value
  const formatCellValue = (value, header) => {
    if (value === undefined || value === null || value === '') {
      return '-';
    }

    // Handle boolean values for is_fixed_payment
    if (typeof value === 'boolean') {
      return value ? 'true' : 'false';
    }

    // Check if this is a date column and value is a number (Excel serial date)
    if (isDateColumn(header) && typeof value === 'number') {
      const dateStr = convertExcelDate(value);
      if (dateStr) return dateStr;
    }

    // Check if value is already a Date object
    if (value instanceof Date) {
      return moment(value).format('DD/MM/YYYY');
    }

    // Handle regular numbers
    if (typeof value === 'number') {
      return value.toFixed(4);
    }

    // Return string as-is
    return value;
  };

  // Generate summary data from full data
  const generateSummaryData = (sheetData) => {
    if (!sheetData || sheetData.length === 0) return null;

    const headers = sheetData[0];
    const rows = sheetData.slice(1);

    // Find column indices
    const forecasterIdx = headers.findIndex(h => String(h).toLowerCase().includes('forecaster'));
    const averageIdx = headers.findIndex(h => String(h).toLowerCase().includes('average'));
    const isFixedPaymentIdx = headers.findIndex(h => String(h).toLowerCase().includes('is_fixed_payment'));

    // Find all date columns (numeric headers)
    const dateColumnIndices = headers
      .map((h, idx) => ({ header: h, idx }))
      .filter(({ header }) => typeof header === 'number' && convertExcelDate(header) !== null)
      .map(({ idx }) => idx);

    // Build summary rows
    const summaryRows = rows.map(row => {
      const forecaster = row[forecasterIdx] || '-';
      const isFixedPayment = isFixedPaymentIdx >= 0 ? row[isFixedPaymentIdx] : null;
      const average = row[averageIdx];

      // Count non-empty values in date columns
      const daysEvaluated = dateColumnIndices.reduce((count, idx) => {
        const value = row[idx];
        if (value !== undefined && value !== null && value !== '' && value !== '-') {
          return count + 1;
        }
        return count;
      }, 0);

      // Format is_fixed_payment to show true/false
      const formattedIsFixedPayment = typeof isFixedPayment === 'boolean'
        ? (isFixedPayment ? 'true' : 'false')
        : (isFixedPayment || '-');

      return [forecaster, formattedIsFixedPayment, average, daysEvaluated];
    });

    // Sort ALL rows (including ensemble) by days evaluated, then by average
    summaryRows.sort((a, b) => {
      const daysA = a[3] || 0;
      const daysB = b[3] || 0;
      const avgA = parseFloat(a[2]) || Infinity;
      const avgB = parseFloat(b[2]) || Infinity;

      // Primary sort: days evaluated (descending - more is better)
      if (daysA !== daysB) {
        return daysB - daysA;
      }
      // Secondary sort: average score (ascending - lower is better)
      return avgA - avgB;
    });

    const sortedRows = summaryRows;

    return {
      headers: ['FORECASTER', 'IS_FIXED_PAYMENT', 'AVERAGE', 'DAYS EVALUATED'],
      rows: sortedRows
    };
  };

  // Render table from parsed Excel data
  const renderTable = () => {
    if (!reportData || sheetNames.length === 0) {
      return (
        <div className="text-center py-12 text-gray-500 dark:text-gray-400">
          No data available
        </div>
      );
    }

    const currentSheetName = sheetNames[activeSheet];
    const sheetData = reportData[currentSheetName];

    if (!sheetData || sheetData.length === 0) {
      return (
        <div className="text-center py-12 text-gray-500 dark:text-gray-400">
          Sheet is empty
        </div>
      );
    }

    // Get data based on view mode
    let headers, rows;
    if (viewMode === 'summary') {
      const summaryData = generateSummaryData(sheetData);
      if (!summaryData) {
        return (
          <div className="text-center py-12 text-gray-500 dark:text-gray-400">
            Unable to generate summary
          </div>
        );
      }
      headers = summaryData.headers;
      rows = summaryData.rows;
    } else {
      // Full view - first row is headers
      headers = sheetData[0];
      const rawRows = sheetData.slice(1);

      // Find column indices
      const averageIdx = headers.findIndex(h => String(h).toLowerCase().includes('average'));

      // Find all date columns (numeric headers) for sorting
      const dateColumnIndices = headers
        .map((h, idx) => ({ header: h, idx }))
        .filter(({ header }) => typeof header === 'number' && convertExcelDate(header) !== null)
        .map(({ idx }) => idx);

      // Calculate days evaluated for each row and sort
      const rowsWithMetrics = rawRows.map(row => {
        const daysEvaluated = dateColumnIndices.reduce((count, idx) => {
          const value = row[idx];
          if (value !== undefined && value !== null && value !== '' && value !== '-') {
            return count + 1;
          }
          return count;
        }, 0);
        const average = row[averageIdx];
        return { row, daysEvaluated, average };
      });

      // Sort ALL rows (including ensemble) by days evaluated, then by average
      rowsWithMetrics.sort((a, b) => {
        const avgA = parseFloat(a.average) || Infinity;
        const avgB = parseFloat(b.average) || Infinity;

        // Primary sort: days evaluated (descending - more is better)
        if (a.daysEvaluated !== b.daysEvaluated) {
          return b.daysEvaluated - a.daysEvaluated;
        }
        // Secondary sort: average score (ascending - lower is better)
        return avgA - avgB;
      });

      rows = rowsWithMetrics.map(({ row }) => row);
    }

    return (
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-100 dark:divide-gray-700">
          <thead className="bg-surface dark:bg-gray-870 sticky top-0 z-10">
            <tr>
              {headers.map((header, idx) => (
                <th
                  key={idx}
                  className={`px-4 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider whitespace-nowrap ${
                    viewMode === 'full' && idx === 0 ? 'sticky left-0 bg-surface dark:bg-gray-870 z-20' : ''
                  }`}
                >
                  {formatHeader(header, idx)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-surface dark:bg-gray-870 divide-y divide-gray-100 dark:divide-gray-700">
            {rows.map((row, rowIdx) => {
              // Check if this row is the ensemble
              const forecasterValue = String(row[0] || '');
              const forecasterValueLower = forecasterValue.toLowerCase();
              const isEnsemble = forecasterValueLower.includes('ensemble');

              return (
                <tr
                  key={rowIdx}
                  className={`${
                    isEnsemble
                      ? 'bg-brand-50 dark:bg-brand-900/20 hover:bg-brand-100 dark:hover:bg-brand-900/30 border-l-4 border-brand-500'
                      : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
                  }`}
                >
                  {headers.map((header, colIdx) => {
                    const cellValue = row[colIdx];
                    // Don't blur ensemble row
                    const shouldBlur = !isEnsemble && blurSensitive &&
                      (String(header).toLowerCase().includes('email') ||
                       String(header).toLowerCase().includes('forecaster'));

                    return (
                      <td
                        key={colIdx}
                        className={`px-4 py-3 text-sm whitespace-nowrap ${
                          isEnsemble ? 'font-semibold text-brand-900 dark:text-brand-100' : 'text-gray-900 dark:text-gray-100'
                        } ${
                          shouldBlur ? 'blur-sm select-none' : ''
                        } ${
                          viewMode === 'full' && colIdx === 0
                            ? `sticky left-0 z-10 ${isEnsemble ? 'bg-brand-50 dark:bg-brand-900/20' : 'bg-surface dark:bg-gray-870'}`
                            : ''
                        }`}
                      >
                        {viewMode === 'summary' ? cellValue : formatCellValue(cellValue, header)}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-surface dark:bg-gray-870 z-50 overflow-hidden flex flex-col" style={SLIDE_OVER_TOP_OFFSET_STYLE}>
      {/* Header */}
      <div className="bg-surface dark:bg-gray-870 border-b border-gray-200/70 dark:border-gray-600/70 px-4 sm:px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={onClose}
              className="p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400"
              title="Back to files"
            >
              <ArrowLeftIcon className="h-5 w-5" />
            </button>
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Market Report
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                {resourceName} • {item.start_date} → {item.end_date}
                {reportData && sheetNames[activeSheet] && (
                  <span className="ml-2">
                    • {reportData[sheetNames[activeSheet]]?.length - 1 || 0} records
                  </span>
                )}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {!loading && !error && (
              <>
                {/* View Mode Toggle */}
                <div className="hidden sm:flex items-center gap-1 border border-gray-300 dark:border-gray-600 rounded-md p-1">
                  <button
                    onClick={() => setViewMode('summary')}
                    className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${
                      viewMode === 'summary'
                        ? 'bg-surface dark:bg-gray-600 text-gray-900 dark:text-gray-100 shadow-sm'
                        : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                    }`}
                  >
                    Summary
                  </button>
                  <button
                    onClick={() => setViewMode('full')}
                    className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${
                      viewMode === 'full'
                        ? 'bg-surface dark:bg-gray-600 text-gray-900 dark:text-gray-100 shadow-sm'
                        : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                    }`}
                  >
                    Full Data
                  </button>
                </div>

                <button
                  onClick={() => onDownload(item, resourceName)}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md border border-gray-300 dark:border-gray-600 bg-surface dark:bg-gray-870 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  <ArrowDownTrayIcon className="h-4 w-4" />
                  <span className="hidden sm:inline">Download</span>
                </button>
              </>
            )}
          </div>
        </div>

        {/* Sheet tabs */}
        {!loading && !error && sheetNames.length > 1 && (
          <div className="flex gap-2 mt-4 overflow-x-auto">
            {sheetNames.map((name, idx) => (
              <button
                key={idx}
                onClick={() => setActiveSheet(idx)}
                className={`px-4 py-2 text-sm font-medium rounded-md whitespace-nowrap transition-colors ${
                  activeSheet === idx
                    ? 'bg-brand-100 dark:bg-brand-900/30 text-brand-700 dark:text-brand-300'
                    : 'border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                {name}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-4 sm:p-6">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <div className="w-12 h-12 border-4 border-gray-200 border-t-brand-600 rounded-full animate-spin mx-auto mb-4" />
              <p className="text-gray-600 dark:text-gray-400">Loading Excel file...</p>
            </div>
          </div>
        ) : error ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <div className="text-red-600 dark:text-red-400 text-lg font-semibold mb-2">Error</div>
              <p className="text-gray-600 dark:text-gray-400 mb-4">{error}</p>
              <button
                onClick={fetchAndParseExcel}
                className="px-4 py-2 border border-brand-500 bg-brand-50 dark:bg-brand-900/30 text-brand-700 dark:text-brand-300 hover:bg-brand-100 dark:hover:bg-brand-900/50 rounded-md transition-colors"
              >
                Retry
              </button>
            </div>
          </div>
        ) : (
          <div className="bg-surface dark:bg-gray-870 rounded-lg shadow-sm border border-gray-200/70 dark:border-gray-600/70">
            {renderTable()}
          </div>
        )}
      </div>
    </div>
  );
};
